import { useCallback, useState } from 'react';

import type { PinVisibilityStorageKey } from '@web/constants/map';
import { getFromStorage, saveInStorage } from '@web/utils/storage';

import type { PinType, PinVisibility } from './mapPins';

const DEFAULT_PIN_VISIBILITY: PinVisibility = {
  plant: true,
  'competitor-plant': true,
  'job-site': true,
};

/**
 * Tracks which pin types a map shows. Persisted per map, because each map
 * serves a different job and users keep their own layer choice for it.
 */
export const usePinVisibility = (
  storageKey: PinVisibilityStorageKey,
): { pinVisibility: PinVisibility; onTogglePinVisibility: (type: PinType) => void } => {
  const [pinVisibility, setPinVisibility] = useState<PinVisibility>(() => ({
    ...DEFAULT_PIN_VISIBILITY,
    ...getFromStorage<Partial<PinVisibility>>(storageKey, {}),
  }));

  const onTogglePinVisibility = useCallback(
    (type: PinType): void => {
      const next = { ...pinVisibility, [type]: !pinVisibility[type] };
      setPinVisibility(next);
      saveInStorage(storageKey, next);
    },
    [pinVisibility, storageKey],
  );

  return { pinVisibility, onTogglePinVisibility };
};
